import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Download, AlertTriangle, FileSpreadsheet, ChevronDown, ChevronUp, Info, Users, UserCheck, Settings2, Image } from "lucide-react";
import AttendanceEditDialog from "./AttendanceEditDialog";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: format date as YYYY-MM-DD using LOCAL timezone (not UTC)
const toLocalDateStr = (d) => {
  if (!d) return '';
  if (typeof d === 'string') {
    // For ISO strings like "2026-03-01T07:00:00+02:00", extract the date part directly
    const match = d.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
    d = new Date(d);
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Romanian National Holidays - returns holidays for a given year
const getRomanianHolidays = (year) => {
  // Fixed holidays
  const fixedHolidays = [
    { date: `${year}-01-01`, name: "Anul Nou" },
    { date: `${year}-01-02`, name: "Anul Nou (zi liberă)" },
    { date: `${year}-01-24`, name: "Ziua Unirii Principatelor Române" },
    { date: `${year}-05-01`, name: "Ziua Muncii" },
    { date: `${year}-06-01`, name: "Ziua Copilului" },
    { date: `${year}-08-15`, name: "Adormirea Maicii Domnului" },
    { date: `${year}-11-30`, name: "Sfântul Andrei" },
    { date: `${year}-12-01`, name: "Ziua Națională a României" },
    { date: `${year}-12-25`, name: "Crăciunul (ziua 1)" },
    { date: `${year}-12-26`, name: "Crăciunul (ziua 2)" },
  ];

  // Calculate Orthodox Easter using Meeus Julian algorithm.
  // Returns the Gregorian-calendar date of Orthodox (Eastern) Easter.
  const calculateOrthodoxEaster = (y) => {
    const a = y % 4;
    const b = y % 7;
    const c = y % 19;
    const d = (19 * c + 15) % 30;
    const e = (2 * a + 4 * b - d + 34) % 7;
    const month = Math.floor((d + e + 114) / 31); // 3 = March, 4 = April
    const day = ((d + e + 114) % 31) + 1;

    // Result is in Julian calendar; convert to Gregorian by adding the
    // current Julian-Gregorian offset (13 days for 1900-2099, 14 days for 2100-2199).
    const julianEaster = new Date(y, month - 1, day);
    const offsetDays = y < 2100 ? 13 : 14;
    julianEaster.setDate(julianEaster.getDate() + offsetDays);
    return julianEaster;
  };

  const easterDate = calculateOrthodoxEaster(year);
  
  // Good Friday (2 days before Easter)
  const goodFriday = new Date(easterDate);
  goodFriday.setDate(goodFriday.getDate() - 2);
  
  // Easter Monday (1 day after Easter)
  const easterMonday = new Date(easterDate);
  easterMonday.setDate(easterMonday.getDate() + 1);
  
  // Pentecost (50 days after Easter Sunday)
  const pentecost = new Date(easterDate);
  pentecost.setDate(pentecost.getDate() + 49);
  
  // Pentecost Monday
  const pentecostMonday = new Date(pentecost);
  pentecostMonday.setDate(pentecostMonday.getDate() + 1);

  const formatDate = (d) => toLocalDateStr(d);

  const variableHolidays = [
    { date: formatDate(goodFriday), name: "Vinerea Mare" },
    { date: formatDate(easterDate), name: "Paștele Ortodox" },
    { date: formatDate(easterMonday), name: "A doua zi de Paște" },
    { date: formatDate(pentecost), name: "Rusaliile" },
    { date: formatDate(pentecostMonday), name: "A doua zi de Rusalii" },
  ];

  return [...fixedHolidays, ...variableHolidays].sort((a, b) => a.date.localeCompare(b.date));
};

// Default leave limits per type (can be configured)
const DEFAULT_LEAVE_LIMITS = {
  CO: 21, // Concediu Odihnă - 21 zile/an
  CM: 180, // Concediu Medical - 180 zile/an
  CS: 5, // Concediu Special - 5 zile/an
  FS: 30, // Fără Salariu - 30 zile/an
};

export default function AttendanceCalendarGrid({ workers, month, year, allData, holidays = [], onRefresh, yearlyLeaveData = {}, companyId, companyName, adminUserId }) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const calendarRef = useRef(null);
  
  // State pentru selecția angajaților care apar în export
  const [selectedForExport, setSelectedForExport] = useState({});
  const [showExportSelection, setShowExportSelection] = useState(false);
  
  // Inițializează selecția - toți angajații sunt selectați implicit (exceptând administratorii)
  useEffect(() => {
    const initialSelection = {};
    workers.forEach(w => {
      // Administratorii sunt deselectați implicit, restul selectați
      initialSelection[w.id] = !w.is_administrator;
    });
    setSelectedForExport(initialSelection);
  }, [workers]);
  
  // Toggle selecție angajat pentru export
  const toggleWorkerExport = (workerId) => {
    setSelectedForExport(prev => ({
      ...prev,
      [workerId]: !prev[workerId]
    }));
  };
  
  // Selectează/Deselectează toți
  const toggleAllForExport = (selectAll) => {
    const newSelection = {};
    workers.forEach(w => {
      newSelection[w.id] = selectAll;
    });
    setSelectedForExport(newSelection);
  };
  
  // Angajații selectați pentru export
  const workersForExport = useMemo(() => workers.filter(w => selectedForExport[w.id]), [workers, selectedForExport]);
  
  // Generate days in month - memoized for performance
  const days = useMemo(() => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const result = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month - 1, day);
      result.push({ day, date, dayOfWeek: date.getDay() });
    }
    return result;
  }, [year, month]);

  const daysInMonth = days.length;

  const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
  
  // Merge passed holidays with Romanian national holidays - memoized
  const { allHolidays, holidayDates } = useMemo(() => {
    const romanianHolidays = getRomanianHolidays(year);
    const merged = [...holidays];
    romanianHolidays.forEach(rh => {
      if (!merged.some(h => h.date === rh.date)) {
        merged.push(rh);
      }
    });
    const dates = new Set(merged.map(h => toLocalDateStr(h.date)));
    return { allHolidays: merged, holidayDates: dates };
  }, [holidays, year]);
  
  const getHolidayName = useCallback((dateStr) => {
    const holiday = allHolidays.find(h => 
      toLocalDateStr(h.date) === dateStr
    );
    return holiday?.name || 'Sărbătoare Legală';
  }, [allHolidays]);

  // Calculate yearly leave usage for a worker - use yearlyLeaveData prop if available
  const getYearlyLeaveUsage = useCallback((userId) => {
    // If we have yearly data from API, use it
    if (yearlyLeaveData && yearlyLeaveData[userId]) {
      return yearlyLeaveData[userId];
    }
    
    // Fallback to calculating from monthly data (less accurate)
    const data = allData[userId];
    if (!data || !data.leaves) return { CO: 0, CM: 0, CS: 0, FS: 0 };
    
    const usage = { CO: 0, CM: 0, CS: 0, FS: 0 };
    
    data.leaves.forEach(leave => {
      if (leave.status === 'approved' && usage[leave.type] !== undefined) {
        usage[leave.type] += leave.days_count || 1;
      }
    });
    
    return usage;
  }, [yearlyLeaveData, allData]);

  // Check if worker exceeded leave limit
  const isOverLeaveLimit = useCallback((userId, leaveType) => {
    const usage = getYearlyLeaveUsage(userId);
    const limit = DEFAULT_LEAVE_LIMITS[leaveType];
    return limit && usage[leaveType] >= limit;
  }, [getYearlyLeaveUsage]);
  
  const getDayStatus = useCallback((date, userId) => {
    if (!allData[userId]) return { type: 'no-data', color: 'bg-white', label: 'Fără date' };
    
    const data = allData[userId];
    const dateStr = toLocalDateStr(date);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const isHolidayDate = holidayDates.has(dateStr) || data.holidays?.some(h => toLocalDateStr(h.date) === dateStr);
    
    // Check overtime FIRST - even on weekends/holidays
    const overtime = data.overtime?.find(o => {
      const oDate = toLocalDateStr(o.date);
      return oDate === dateStr;
    });
    if (overtime) {
      return { 
        type: 'overtime', 
        color: 'bg-amber-500', 
        label: `OS ${overtime.hours}h`,
        hours: overtime.hours,
        equivalentHours: overtime.equivalent_hours,
        isOvertime: true,
        isOnHoliday: isHolidayDate,
        isOnWeekend: isWeekend
      };
    }
    
    // Check if weekend (no overtime)
    if (isWeekend) {
      return { type: 'weekend', color: 'bg-slate-200', label: 'Weekend' };
    }
    
    // Check global holidays first (no overtime)
    if (holidayDates.has(dateStr)) {
      return { 
        type: 'holiday', 
        color: 'bg-yellow-400', 
        label: getHolidayName(dateStr),
        isHoliday: true 
      };
    }
    
    // Check holidays from user data
    const holiday = data.holidays?.find(h => {
      const hDate = toLocalDateStr(h.date);
      return hDate === dateStr;
    });
    if (holiday) {
      return { 
        type: 'holiday', 
        color: 'bg-yellow-400', 
        label: holiday.name,
        isHoliday: true 
      };
    }
    
    // Check leaves
    const leave = data.leaves?.find(l => {
      const start = toLocalDateStr(l.start_date);
      const end = toLocalDateStr(l.end_date);
      return dateStr >= start && dateStr <= end && l.status === 'approved';
    });
    if (leave) {
      const colors = {
        'CO': 'bg-orange-400',
        'CM': 'bg-red-400',
        'CS': 'bg-purple-400',
        'FS': 'bg-pink-400'
      };
      const overLimit = isOverLeaveLimit(userId, leave.type);
      return { 
        type: leave.type, 
        color: overLimit ? 'bg-red-600 animate-pulse' : colors[leave.type] || 'bg-orange-400', 
        label: leave.type,
        isDraggable: true,
        leaveId: leave.id,
        overLimit
      };
    }
    
    // Check absences
    const absence = data.absences?.find(a => {
      const aDate = toLocalDateStr(a.date);
      return aDate === dateStr;
    });
    if (absence) {
      const colors = {
        'absence': 'bg-gray-600',
        'delegate': 'bg-blue-400',
        'invoire': 'bg-cyan-400',
        'medical': 'bg-red-400'
      };
      const labels = {
        'absence': 'Absent',
        'delegate': 'Delegație',
        'invoire': 'Învoire',
        'medical': 'Medical'
      };
      return { 
        type: absence.type, 
        color: colors[absence.type] || 'bg-gray-600', 
        label: labels[absence.type] || absence.type,
        isDraggable: true
      };
    }
    
    // Check attendance
    const attendance = data.attendances?.find(a => {
      const aDate = toLocalDateStr(a.check_in);
      return aDate === dateStr;
    });
    if (attendance) {
      return { 
        type: 'present', 
        color: attendance.is_auto_generated ? 'bg-green-300' : 'bg-green-500', 
        label: attendance.is_auto_generated ? 'Auto' : 'Prezent',
        hours: attendance.hours_worked,
        isAutoGenerated: attendance.is_auto_generated
      };
    }
    
    // No data - empty (overtime is already checked at the beginning)
    return { type: 'no-data', color: 'bg-white border border-slate-200', label: 'Lipsă' };
  }, [allData, holidayDates]);

  const handleCellClick = (worker, date, status) => {
    // Allow clicking on any day including weekends (for overtime)
    setSelectedWorker(worker);
    setSelectedDate(date);
    setSelectedStatus(status);
    setEditDialogOpen(true);
  };

  const handleSave = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  // Asigură plata o singură dată per lună pentru emiterea pontajului.
  // Apelat înainte de orice export (PDF, Visual PDF, Excel). Backend-ul
  // deduplică prin combinația (company_id, action_code, reference_id).
  const ensurePontajPaid = async () => {
    const refId = `${year}-${String(month).padStart(2, '0')}`;
    try {
      const r = await axios.post(
        `${API}/credits/deduct`,
        {
          company_id: companyId,
          action_code: 'generate_pontaj_monthly',
          reference_id: refId,
          description: `Pontaj lunar ${refId}`,
        },
        adminUserId ? { params: { user_id: adminUserId } } : {}
      );
      if (r.data?.already_paid) {
        toast.info('Pontajul pentru această lună a fost deja achitat — descărcare gratuită.');
      } else if (r.data?.deducted > 0) {
        toast.success(`S-au dedus ${r.data.deducted} credite pentru pontajul lunii ${refId}.`);
      }
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402) {
        toast.error(
          (detail && detail.message)
            ? `Credite insuficiente (necesare: ${detail.required}, disponibile: ${detail.available}). Solicită credite din meniul Plăți.`
            : 'Credite insuficiente pentru emiterea pontajului.'
        );
      } else {
        toast.error('Nu s-a putut verifica plata pontajului. Încearcă din nou.');
      }
      return false;
    }
  };

  // Sanitize company name for use in filenames (păstrează diacritice ASCII).
  const safeCompanyFileSlug = (companyName || 'pontaj').replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'pontaj';

  // Export to PDF - Using jspdf-autotable for proper table layout
  const exportToPDF = async () => {
    if (!(await ensurePontajPaid())) return;
    setExporting(true);
    toast.info("Se generează PDF-ul...");
    
    try {
      // Import autoTable dynamically - use named import for v5.x
      const { autoTable } = await import('jspdf-autotable');
      
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const monthName = new Date(year, month - 1).toLocaleString('ro-RO', { month: 'long', year: 'numeric' });
      const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
      
      // === Fetch signatures for all workers ===
      let signaturesMap = {};
      try {
        const sigResponse = await fetch(`${API}/signatures/company/${companyId}/${month}/${year}`);
        if (sigResponse.ok) {
          const signatures = await sigResponse.json();
          signatures.forEach(sig => {
            signaturesMap[sig.user_id] = sig;
          });
        }
      } catch (sigErr) {
        console.log('Could not fetch signatures:', sigErr);
      }
      
      // === Header Section ===
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Pontaj ${capitalizedMonth}`, pdfWidth / 2, 10, { align: 'center' });
      
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Pag.: 1/1`, pdfWidth - 15, 10, { align: 'right' });
      
      // === Build Table Data ===
      // Header row 1: Day names + Signature column
      const headerRow1 = ['Nr.', 'Angajat'];
      // First 15 days
      for (let d = 1; d <= 15; d++) {
        const date = new Date(year, month - 1, d);
        headerRow1.push(dayNames[date.getDay()]);
      }
      headerRow1.push('Ore\n1-15');
      // Days 16 to end
      for (let d = 16; d <= daysInMonth; d++) {
        const date = new Date(year, month - 1, d);
        headerRow1.push(dayNames[date.getDay()]);
      }
      headerRow1.push('Ore\n16-31', 'Ore\n1-31', 'Zile\nlucr.', 'CO', 'CM', 'OS', 'Semn.');
      
      // Header row 2: Day numbers
      const headerRow2 = ['', ''];
      for (let d = 1; d <= 15; d++) {
        headerRow2.push(d.toString());
      }
      headerRow2.push('');
      for (let d = 16; d <= daysInMonth; d++) {
        headerRow2.push(d.toString());
      }
      headerRow2.push('', '', '', '', '', '', '');
      
      // Data rows - store worker info for signature rendering
      const tableData = [];
      const workerSignatures = []; // Store signatures with row index
      
      workersForExport.forEach((worker, idx) => {
        const data = allData[worker.id] || {};
        const row = [idx + 1, worker.name]; // Full name
        
        let hours1to15 = 0;
        let hours16to31 = 0;
        let zileLucrate = 0;
        let zileCO = 0;
        let zileCM = 0;
        let oreSuplimentare = 0;
        
        // Process each day
        for (let d = 1; d <= daysInMonth; d++) {
          const date = new Date(year, month - 1, d);
          const status = getDayStatus(date, worker.id);
          let cellValue = '';
          let hoursForDay = 0;
          
          if (status.type === 'weekend') {
            cellValue = '';
          } else if (status.type === 'holiday') {
            cellValue = 'SL';
          } else if (status.type === 'overtime') {
            cellValue = status.hours ? `${status.hours}` : 'OS';
            oreSuplimentare += status.hours || 0;
          } else if (status.type === 'present') {
            hoursForDay = status.hours || 8;
            cellValue = hoursForDay.toString();
            zileLucrate++;
          } else if (status.type === 'CO') {
            cellValue = 'Co';
            zileCO++;
          } else if (status.type === 'CM') {
            cellValue = 'Bo';
            zileCM++;
          } else if (['CS', 'FS'].includes(status.type)) {
            cellValue = status.type;
          } else if (status.type === 'absence') {
            cellValue = 'A';
          } else if (status.type === 'invoire') {
            cellValue = 'I';
          } else if (status.type === 'delegate') {
            cellValue = 'D';
          } else {
            cellValue = '';
          }
          
          // Add hours to appropriate sum
          if (d <= 15) {
            hours1to15 += hoursForDay;
          } else {
            hours16to31 += hoursForDay;
          }
          
          row.push(cellValue);
          
          // Insert "Ore 1-15" column after day 15
          if (d === 15) {
            row.push(hours1to15 > 0 ? hours1to15.toFixed(0) : '');
          }
        }
        
        // Add summary columns + signature status
        const totalHours = hours1to15 + hours16to31;
        const workerSignature = signaturesMap[worker.id];
        const signatureStatus = workerSignature ? '✓' : '-';
        
        row.push(
          hours16to31 > 0 ? hours16to31.toFixed(0) : '',
          totalHours > 0 ? totalHours.toFixed(0) : '',
          zileLucrate > 0 ? zileLucrate.toString() : '',
          zileCO > 0 ? zileCO.toString() : '',
          zileCM > 0 ? zileCM.toString() : '',
          oreSuplimentare > 0 ? oreSuplimentare.toFixed(0) : '',
          signatureStatus
        );
        
        // Store signature info for later rendering
        if (workerSignature) {
          workerSignatures.push({
            rowIndex: idx,
            signature: workerSignature
          });
        }
        
        tableData.push(row);
      });
      
      // === Calculate column widths ===
      const numDays = daysInMonth;
      const nameColWidth = 35; // Wider for full names
      const nrColWidth = 8;
      const dayColWidth = 6;
      const summaryColWidth = 10;
      const signatureColWidth = 8; // Signature column
      
      const columnStyles = {
        0: { cellWidth: nrColWidth, halign: 'center' }, // Nr.
        1: { cellWidth: nameColWidth, halign: 'left' }, // Angajat (full name)
      };
      
      // Day columns (1-15)
      for (let i = 2; i <= 16; i++) {
        columnStyles[i] = { cellWidth: dayColWidth, halign: 'center' };
      }
      // Ore 1-15
      columnStyles[17] = { cellWidth: summaryColWidth, halign: 'center' };
      // Day columns (16-31)
      for (let i = 18; i <= 17 + (daysInMonth - 15); i++) {
        columnStyles[i] = { cellWidth: dayColWidth, halign: 'center' };
      }
      // Summary columns (6 columns) + Signature column (1)
      const lastDayCol = 17 + (daysInMonth - 15);
      for (let i = lastDayCol + 1; i <= lastDayCol + 6; i++) {
        columnStyles[i] = { cellWidth: summaryColWidth, halign: 'center' };
      }
      // Signature column
      columnStyles[lastDayCol + 7] = { cellWidth: signatureColWidth, halign: 'center' };
      
      // === Generate Table ===
      autoTable(pdf, {
        head: [headerRow1, headerRow2],
        body: tableData,
        startY: 15,
        theme: 'grid',
        styles: {
          fontSize: 6,
          cellPadding: 1,
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          overflow: 'linebreak',
          valign: 'middle',
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
          fontSize: 6,
        },
        columnStyles: columnStyles,
        didParseCell: function(data) {
          const cell = data.cell;
          const text = cell.raw?.toString() || '';
          
          // Weekend columns - light gray background
          if (data.section === 'body' && data.column.index >= 2) {
            const colIdx = data.column.index;
            let dayNum = null;
            
            if (colIdx >= 2 && colIdx <= 16) {
              dayNum = colIdx - 1; // Days 1-15
            } else if (colIdx >= 18 && colIdx <= 17 + (daysInMonth - 15)) {
              dayNum = colIdx - 18 + 16; // Days 16-31
            }
            
            if (dayNum && dayNum <= daysInMonth) {
              const date = new Date(year, month - 1, dayNum);
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              const dateStr = toLocalDateStr(date);
              const isHoliday = holidayDates.has(dateStr);
              
              if (isWeekend) {
                cell.styles.fillColor = [220, 220, 220];
              } else if (isHoliday) {
                cell.styles.fillColor = [255, 255, 200];
              }
            }
          }
          
          // Color coding for specific values
          if (text === 'SL') {
            cell.styles.fillColor = [255, 255, 150];
            cell.styles.fontStyle = 'bold';
          } else if (text === 'Co') {
            cell.styles.fillColor = [255, 200, 150];
          } else if (text === 'Bo') {
            cell.styles.fillColor = [255, 180, 180];
          } else if (text === 'A') {
            cell.styles.fillColor = [200, 200, 200];
          } else if (text === '✓') {
            // Signature present - green background
            cell.styles.fillColor = [150, 230, 150];
            cell.styles.fontStyle = 'bold';
            cell.styles.textColor = [0, 100, 0];
          } else if (text.match(/^\d+$/) && parseInt(text) > 0 && data.column.index >= 2 && data.column.index <= 17 + (daysInMonth - 15)) {
            // Worked hours - light green
            cell.styles.fillColor = [200, 255, 200];
          }
        },
        margin: { top: 15, left: 5, right: 5, bottom: 25 },
      });
      
      // === Legend Section ===
      const finalY = pdf.lastAutoTable?.finalY || 150;
      const legendY = Math.min(finalY + 5, pdfHeight - 30);
      
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Legendă:', 10, legendY);
      pdf.text('Co - Concediu odihnă | Bo - Boală (CM) | SL - Sărbătoare Legală | OS - Ore Suplimentare | A - Absent | I - Învoire | D - Delegație | ✓ - Semnat digital', 10, legendY + 4);
      
      // === Signatures Section (using already fetched signatures) ===
      const uniqueSignatures = Object.values(signaturesMap);
      console.log('Signatures to add to PDF:', uniqueSignatures.length, uniqueSignatures);
      
      if (uniqueSignatures.length > 0) {
        // Add new page for signatures
        pdf.addPage();
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Semnături Pontaj - ${capitalizedMonth}`, pdfWidth / 2, 15, { align: 'center' });
        
        let sigY = 25;
        const sigWidth = 65;
        const sigHeight = 30;
        const sigsPerRow = 4;
        const sigMargin = 8;
        
        for (let i = 0; i < uniqueSignatures.length; i++) {
          const sig = uniqueSignatures[i];
          const col = i % sigsPerRow;
          const rowNum = Math.floor(i / sigsPerRow);
          const x = sigMargin + col * (sigWidth + 3);
          const y = sigY + rowNum * (sigHeight + 20);
          
          // Check if we need a new page
          if (y + sigHeight + 20 > pdfHeight - 25) {
            pdf.addPage();
            sigY = 15;
          }
          
          // Draw signature box with border
          pdf.setDrawColor(80, 80, 80);
          pdf.setLineWidth(0.4);
          pdf.rect(x, y, sigWidth, sigHeight);
          
          // Add white background
          pdf.setFillColor(255, 255, 255);
          pdf.rect(x + 0.2, y + 0.2, sigWidth - 0.4, sigHeight - 0.4, 'F');
          
          // Add signature image
          if (sig.signature_image && sig.signature_image.length > 100) {
            try {
              // Ensure the image data is properly formatted
              let imageData = sig.signature_image;
              if (!imageData.startsWith('data:')) {
                imageData = 'data:image/png;base64,' + imageData;
              }
              pdf.addImage(imageData, 'PNG', x + 1, y + 1, sigWidth - 2, sigHeight - 2);
              console.log('Added signature image for:', sig.user_name);
            } catch (imgError) {
              console.error('Error adding signature image:', imgError, sig.user_name);
              // Draw placeholder text
              pdf.setFontSize(8);
              pdf.setTextColor(150, 150, 150);
              pdf.text('[Semnătură]', x + sigWidth / 2, y + sigHeight / 2, { align: 'center' });
              pdf.setTextColor(0, 0, 0);
            }
          } else {
            // No valid signature image
            pdf.setFontSize(8);
            pdf.setTextColor(150, 150, 150);
            pdf.text('[Fără imagine]', x + sigWidth / 2, y + sigHeight / 2, { align: 'center' });
            pdf.setTextColor(0, 0, 0);
          }
          
          // Add name below signature
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0);
          const displayName = sig.user_name || 'N/A';
          pdf.text(displayName.substring(0, 20), x + sigWidth / 2, y + sigHeight + 4, { align: 'center' });
          
          // Add date and count
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(6);
          const signedDate = sig.signed_at ? new Date(sig.signed_at).toLocaleString('ro-RO', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : '';
          pdf.text(`Semnat: ${signedDate}`, x + sigWidth / 2, y + sigHeight + 8, { align: 'center' });
          
          // Add signature count and IP
          if (sig.signature_count) {
            pdf.setFontSize(5);
            pdf.setTextColor(100, 100, 100);
            pdf.text(`(${sig.signature_count}/2) | IP: ${sig.ip_address || 'N/A'}`, x + sigWidth / 2, y + sigHeight + 12, { align: 'center' });
            pdf.setTextColor(0, 0, 0);
          }
        }
        
        // Add signature verification note at bottom
        pdf.setFontSize(6);
        pdf.setFont('helvetica', 'italic');
        pdf.setTextColor(80, 80, 80);
        const noteY = pdfHeight - 12;
        pdf.text('Semnăturile digitale sunt înregistrate conform Regulamentului eIDAS și Legii 455/2001.', pdfWidth / 2, noteY, { align: 'center' });
        pdf.text('Pentru fiecare semnătură s-au înregistrat: data, ora, dispozitivul și adresa IP.', pdfWidth / 2, noteY + 4, { align: 'center' });
        pdf.setTextColor(0, 0, 0);
      }
      
      // === Footer ===
      pdf.setFontSize(7);
      pdf.text(`Generat la: ${new Date().toLocaleString('ro-RO')}`, 10, pdfHeight - 8);
      pdf.text('APP PONTAJ', pdfWidth - 10, pdfHeight - 8, { align: 'right' });
      
      // Save
      pdf.save(`${safeCompanyFileSlug}_Pontaj_${capitalizedMonth.replace(' ', '_')}.pdf`);
      toast.success("PDF generat cu succes!");
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Eroare la generarea PDF-ului: " + error.message);
    } finally {
      setExporting(false);
    }
  };

  // Export to Visual PDF - captures the calendar grid as-is with colors
  const exportToVisualPDF = async () => {
    if (!calendarRef.current) return;
    if (!(await ensurePontajPaid())) return;
    setExporting(true);
    toast.info("Se generează PDF-ul grafic...");
    try {
      const monthName = new Date(year, month - 1).toLocaleString('ro-RO', { month: 'long', year: 'numeric' });
      const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
      const pdfTitle = companyName ? `${companyName} Pontaj ${capitalizedMonth}` : `Pontaj ${capitalizedMonth}`;
      
      // Fetch employee signatures
      let signaturesMap = {};
      try {
        const sigResponse = await fetch(`${API}/signatures/company/${companyId}/${month}/${year}`);
        if (sigResponse.ok) {
          const signatures = await sigResponse.json();
          signatures.forEach(sig => { signaturesMap[sig.user_id] = sig; });
        }
      } catch (sigErr) {
        console.log('Could not fetch signatures for visual PDF:', sigErr);
      }
      
      // Fetch admin signature
      let adminSignature = null;
      if (adminUserId) {
        try {
          const adminSigResp = await fetch(`${API}/signatures/company/${companyId}/${month}/${year}`);
          if (adminSigResp.ok) {
            const allSigs = await adminSigResp.json();
            adminSignature = allSigs.find(s => s.user_id === adminUserId);
          }
        } catch (e) {
          console.log('Could not fetch admin signature:', e);
        }
      }
      
      // Load Roboto fonts for Romanian diacritics
      const loadFont = async (url) => {
        const resp = await fetch(url);
        const buf = await resp.arrayBuffer();
        const bytes = new Uint8Array(buf);
        // Convert to base64 in chunks to avoid stack overflow
        let binary = '';
        const chunkSize = 8192;
        for (let i = 0; i < bytes.length; i += chunkSize) {
          binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
        }
        return btoa(binary);
      };
      const [robotoRegB64, robotoBoldB64] = await Promise.all([
        loadFont('/fonts/Roboto-Regular.ttf'),
        loadFont('/fonts/Roboto-Bold.ttf'),
      ]);
      
      const gridEl = calendarRef.current;
      
      // Inject Roboto font-face for html2canvas rendering
      const fontFaceCSS = `
        @font-face {
          font-family: 'RobotoPDF';
          src: url('/fonts/Roboto-Regular.ttf') format('truetype');
          font-weight: normal;
        }
        @font-face {
          font-family: 'RobotoPDF';
          src: url('/fonts/Roboto-Bold.ttf') format('truetype');
          font-weight: bold;
        }
      `;
      const styleEl = document.createElement('style');
      styleEl.textContent = fontFaceCSS;
      document.head.appendChild(styleEl);
      
      // Apply Roboto font to grid for capture & increase row heights
      gridEl.style.fontFamily = "'RobotoPDF', 'Roboto', sans-serif";
      
      // Ensure all text elements use Roboto and are visible
      const textEls = gridEl.querySelectorAll('span, td, th, div');
      const origFonts = [];
      textEls.forEach(el => {
        origFonts.push(el.style.fontFamily);
        el.style.fontFamily = "'RobotoPDF', 'Roboto', sans-serif";
      });
      
      // Hide avatar circles and style name cells for PDF
      const avatarEls = gridEl.querySelectorAll('.rounded-full');
      const origAvatarDisplay = [];
      avatarEls.forEach(el => {
        origAvatarDisplay.push(el.style.display);
        el.style.display = 'none';
      });
      
      // Make name spans uppercase with Times New Roman, remove truncation
      const nameSpans = gridEl.querySelectorAll('td .truncate');
      const origNameStyles = [];
      nameSpans.forEach(el => {
        origNameStyles.push({
          textTransform: el.style.textTransform,
          fontFamily: el.style.fontFamily,
          maxWidth: el.style.maxWidth,
          overflow: el.style.overflow,
          fontSize: el.style.fontSize,
          fontWeight: el.style.fontWeight,
          whiteSpace: el.style.whiteSpace,
        });
        el.style.textTransform = 'uppercase';
        el.style.fontFamily = "'Times New Roman', 'RobotoPDF', serif";
        el.style.maxWidth = 'none';
        el.style.overflow = 'visible';
        el.style.fontSize = '11px';
        el.style.fontWeight = 'bold';
        el.style.whiteSpace = 'nowrap';
      });
      
      // Style name cells as filled boxes
      const nameCells = gridEl.querySelectorAll('td.sticky');
      const origNameCellStyles = [];
      nameCells.forEach(el => {
        origNameCellStyles.push({
          bg: el.style.backgroundColor,
          border: el.style.border,
          padding: el.style.padding,
        });
        el.style.backgroundColor = '#f0f4ff';
        el.style.border = '1px solid #93a7e1';
        el.style.padding = '6px 8px';
      });
      
      // Save original styles
      const origOverflow = gridEl.style.overflow;
      const origWidth = gridEl.style.width;
      const origMaxWidth = gridEl.style.maxWidth;
      const origFontFamily = gridEl.style.fontFamily;
      
      // Temporarily remove sticky positioning to prevent clipping in html2canvas
      const stickyEls = gridEl.querySelectorAll('.sticky');
      const origPositions = [];
      stickyEls.forEach(el => {
        origPositions.push({ el, pos: el.style.position, left: el.style.left, zIndex: el.style.zIndex });
        el.style.position = 'relative';
        el.style.left = 'auto';
        el.style.zIndex = 'auto';
      });
      
      // Expand grid
      gridEl.style.overflow = 'visible';
      gridEl.style.width = 'max-content';
      gridEl.style.maxWidth = 'none';
      
      // Temporarily increase row heights by 30% for PDF capture
      const allRows = gridEl.querySelectorAll('tr');
      const origRowHeights = [];
      allRows.forEach(row => {
        origRowHeights.push(row.style.height);
        const currentH = row.getBoundingClientRect().height;
        row.style.height = `${Math.round(currentH * 1.3)}px`;
      });
      const allCells = gridEl.querySelectorAll('td, th');
      const origCellPaddings = [];
      allCells.forEach(cell => {
        origCellPaddings.push(cell.style.padding);
        cell.style.padding = '6px 4px';
      });
      
      // Wait for font to load and layout to stabilize
      await document.fonts.ready;
      await new Promise(r => setTimeout(r, 300));
      
      const canvas = await html2canvas(gridEl, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: gridEl.scrollWidth + 60,
      });
      
      // Restore all styles
      textEls.forEach((el, i) => { el.style.fontFamily = origFonts[i]; });
      avatarEls.forEach((el, i) => { el.style.display = origAvatarDisplay[i]; });
      nameSpans.forEach((el, i) => {
        el.style.textTransform = origNameStyles[i].textTransform;
        el.style.fontFamily = origNameStyles[i].fontFamily;
        el.style.maxWidth = origNameStyles[i].maxWidth;
        el.style.overflow = origNameStyles[i].overflow;
        el.style.fontSize = origNameStyles[i].fontSize;
        el.style.fontWeight = origNameStyles[i].fontWeight;
        el.style.whiteSpace = origNameStyles[i].whiteSpace;
      });
      nameCells.forEach((el, i) => {
        el.style.backgroundColor = origNameCellStyles[i].bg;
        el.style.border = origNameCellStyles[i].border;
        el.style.padding = origNameCellStyles[i].padding;
      });
      document.head.removeChild(styleEl);
      allRows.forEach((row, i) => { row.style.height = origRowHeights[i]; });
      allCells.forEach((cell, i) => { cell.style.padding = origCellPaddings[i]; });
      
      // Restore all styles
      stickyEls.forEach((el, i) => {
        el.style.position = origPositions[i].pos;
        el.style.left = origPositions[i].left;
        el.style.zIndex = origPositions[i].zIndex;
      });
      gridEl.style.overflow = origOverflow;
      gridEl.style.width = origWidth;
      gridEl.style.maxWidth = origMaxWidth;
      gridEl.style.fontFamily = '';
      
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const imgW = canvas.width;
      const imgH = canvas.height;
      
      // Landscape A4
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const headerH = 14;
      
      // === PAGE 1: Calendar Grid ===
      pdf.setFontSize(13);
      pdf.setFont('Roboto', 'bold');
      pdf.text(pdfTitle, pdfW / 2, 9, { align: 'center' });
      
      const availW = pdfW - margin * 2;
      const availH = pdfH - headerH - margin - 10;
      const ratio = Math.min(availW / imgW, availH / imgH);
      const drawW = imgW * ratio;
      const drawH = imgH * ratio;
      const offsetX = margin + (availW - drawW) / 2;
      
      pdf.addImage(imgData, 'JPEG', offsetX, headerH, drawW, drawH);
      
      // Legend
      const legendY = headerH + drawH + 3;
      if (legendY < pdfH - 6) {
        pdf.setFontSize(6);
        pdf.setFont('Roboto', 'normal');
        const legendItems = [
          { color: [34,197,94], label: 'Prezent' },
          { color: [134,239,172], label: 'Auto' },
          { color: [245,158,11], label: 'OS' },
          { color: [251,146,60], label: 'CO' },
          { color: [248,113,113], label: 'CM' },
          { color: [192,132,252], label: 'CS' },
          { color: [244,114,182], label: 'FS' },
          { color: [103,232,249], label: '\u00CEnv.' },
          { color: [96,165,250], label: 'Del.' },
          { color: [75,85,99], label: 'Abs.' },
          { color: [250,204,21], label: 'SL' },
          { color: [203,213,225], label: 'Weekend' },
        ];
        let lx = margin;
        legendItems.forEach(item => {
          pdf.setFillColor(...item.color);
          pdf.rect(lx, legendY, 3, 3, 'F');
          pdf.text(item.label, lx + 4, legendY + 2.5);
          lx += pdf.getTextWidth(item.label) + 7;
        });
      }
      
      // === PAGE 2: Summary Cards + Signatures ===
      pdf.addPage('a4', 'landscape');
      pdf.setFontSize(12);
      pdf.setFont('Roboto', 'bold');
      pdf.text(`${pdfTitle} - Sumar \u0219i Semn\u0103turi`, pdfW / 2, 10, { align: 'center' });
      
      const cardMargin = 8;
      const cols = Math.min(workers.length, 3);
      const cardW = (pdfW - cardMargin * 2 - (cols - 1) * 5) / cols;
      const cardH = 95;
      let baseCardY = 16;
      
      workers.forEach((worker, idx) => {
        const col = idx % cols;
        const row = Math.floor(idx / cols);
        const y = baseCardY + row * (cardH + 4);
        
        if (y + cardH > pdfH - 35) {
          pdf.addPage('a4', 'landscape');
          baseCardY = 12 - row * (cardH + 4);
        }
        
        const x = cardMargin + col * (cardW + 5);
        const cy = baseCardY + row * (cardH + 4);
        
        const data = allData[worker.id] || {};
        
        // Compute stats from getDayStatus() to match grid exactly
        let presentDays = 0;
        let leaveDays = 0;
        let totalHours = 0;
        let absenceDays = 0;
        let overtimeHoursCalc = 0;
        let overtimeEquivCalc = 0;
        let overtimeOnWeekendCalc = false;
        let overtimeOnHolidayCalc = false;
        
        days.forEach(d => {
          const status = getDayStatus(d.date, worker.id);
          if (status.type === 'present') {
            presentDays++;
            totalHours += status.hours || 8;
          } else if (status.type === 'overtime') {
            overtimeHoursCalc += status.hours || 0;
            overtimeEquivCalc += status.equivalentHours || (status.hours || 0) * 1.5;
            if (status.isOnWeekend) overtimeOnWeekendCalc = true;
            if (status.isOnHoliday) overtimeOnHolidayCalc = true;
          } else if (['CO', 'CM', 'CS', 'FS'].includes(status.type)) {
            leaveDays++;
          } else if (status.type === 'absence') {
            absenceDays++;
          }
        });
        
        const overtimeHours = overtimeHoursCalc;
        const overtimeEquivalent = overtimeEquivCalc;
        const showOvertime = overtimeHours > 0 && (totalHours > 160 || overtimeOnWeekendCalc || overtimeOnHolidayCalc);
        
        // Card
        pdf.setFillColor(248, 250, 252);
        pdf.setDrawColor(203, 213, 225);
        pdf.roundedRect(x, cy, cardW, cardH, 2, 2, 'FD');
        
        // Avatar
        const initials = worker.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
        pdf.setFillColor(20, 184, 166);
        pdf.circle(x + 7, cy + 7, 4, 'F');
        pdf.setFontSize(6);
        pdf.setFont('Roboto', 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.text(initials, x + 7, cy + 8.5, { align: 'center' });
        
        // Name
        pdf.setTextColor(30, 41, 59);
        pdf.setFontSize(9);
        pdf.setFont('Roboto', 'bold');
        pdf.text(worker.name, x + 14, cy + 8.5);
        
        pdf.setDrawColor(226, 232, 240);
        pdf.line(x + 3, cy + 13, x + cardW - 3, cy + 13);
        
        // Stats
        pdf.setFont('Roboto', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(71, 85, 105);
        
        const stats = [
          { label: 'Zile prezent:', value: `${presentDays}`, color: [22, 163, 74] },
          { label: 'Ore lucrate:', value: `${totalHours.toFixed(1)}h`, color: [37, 99, 235] },
          { label: 'Zile concediu:', value: `${leaveDays}`, color: [234, 88, 12] },
          { label: 'Zile absen\u021be:', value: `${absenceDays}`, color: [107, 114, 128] },
        ];
        if (showOvertime) {
          stats.push({ label: 'Ore suplimentare:', value: `${overtimeHours.toFixed(1)}h (x1.5 = ${overtimeEquivalent.toFixed(1)}h)`, color: [217, 119, 6] });
        }
        
        let sy = cy + 18;
        stats.forEach(s => {
          pdf.setFont('Roboto', 'normal');
          pdf.setTextColor(71, 85, 105);
          pdf.text(s.label, x + 5, sy);
          pdf.setFont('Roboto', 'bold');
          pdf.setTextColor(...s.color);
          pdf.text(s.value, x + cardW - 5, sy, { align: 'right' });
          sy += 5;
        });
        
        // Employee signature section
        const sigStartY = cy + 46;
        pdf.setDrawColor(203, 213, 225);
        pdf.line(x + 3, sigStartY, x + cardW - 3, sigStartY);
        
        pdf.setFont('Roboto', 'normal');
        pdf.setFontSize(6.5);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Semn\u0103tura angajat:', x + 5, sigStartY + 4);
        
        // Try to add signature image
        const workerSig = signaturesMap[worker.id];
        if (workerSig && workerSig.signature_image && workerSig.signature_image.length > 100) {
          try {
            let imgData = workerSig.signature_image;
            if (!imgData.startsWith('data:')) {
              imgData = 'data:image/png;base64,' + imgData;
            }
            const sigBoxW = cardW - 10;
            const sigBoxH = 30;
            // Signature image box
            pdf.setDrawColor(220, 225, 235);
            pdf.setFillColor(255, 255, 255);
            pdf.roundedRect(x + 5, sigStartY + 6, sigBoxW, sigBoxH, 1, 1, 'FD');
            pdf.addImage(imgData, 'PNG', x + 6, sigStartY + 7, sigBoxW - 2, sigBoxH - 2);
          } catch (imgErr) {
            // Fallback: dotted line
            pdf.setDrawColor(148, 163, 184);
            pdf.setLineDashPattern([1, 1], 0);
            pdf.line(x + 5, sigStartY + 30, x + cardW - 5, sigStartY + 30);
            pdf.setLineDashPattern([], 0);
          }
        } else {
          // No signature - dotted line placeholder
          pdf.setDrawColor(148, 163, 184);
          pdf.setLineDashPattern([1, 1], 0);
          pdf.line(x + 5, sigStartY + 30, x + cardW - 5, sigStartY + 30);
          pdf.setLineDashPattern([], 0);
          pdf.setFont('Roboto', 'normal');
          pdf.setFontSize(6);
          pdf.setTextColor(180, 180, 190);
          pdf.text('[f\u0103r\u0103 semn\u0103tur\u0103]', x + cardW / 2, sigStartY + 22, { align: 'center' });
        }
      });
      
      // === Admin Signature Block ===
      const lastRow = Math.floor((workers.length - 1) / cols);
      let adminY = baseCardY + (lastRow + 1) * (cardH + 4) + 4;
      const adminBlockH = adminSignature ? 50 : 28;
      if (adminY + adminBlockH > pdfH - 8) {
        pdf.addPage('a4', 'landscape');
        adminY = 14;
      }
      
      const adminW = pdfW - cardMargin * 2;
      pdf.setFillColor(240, 245, 255);
      pdf.setDrawColor(147, 167, 225);
      pdf.roundedRect(cardMargin, adminY, adminW, adminBlockH, 2, 2, 'FD');
      
      pdf.setFont('Roboto', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(30, 58, 138);
      pdf.text('Aprobat de Administrator', cardMargin + 5, adminY + 6);
      
      pdf.setFont('Roboto', 'normal');
      pdf.setFontSize(7.5);
      pdf.setTextColor(71, 85, 105);
      pdf.text(`Nume: ${companyName || '_______________'}`, cardMargin + 5, adminY + 13);
      
      if (adminSignature && adminSignature.signed_at) {
        const signedDate = new Date(adminSignature.signed_at);
        const dateFormatted = `${signedDate.getDate().toString().padStart(2,'0')}.${(signedDate.getMonth()+1).toString().padStart(2,'0')}.${signedDate.getFullYear()}`;
        pdf.text(`Data semn\u0103rii: ${dateFormatted}`, cardMargin + 5, adminY + 19);
      } else {
        pdf.text('Data: ___.___.______', cardMargin + 5, adminY + 19);
      }
      
      // Admin signature area
      pdf.text('Semn\u0103tura:', cardMargin + adminW / 2, adminY + 6);
      
      if (adminSignature && adminSignature.signature_image && adminSignature.signature_image.length > 100) {
        try {
          let sigImg = adminSignature.signature_image;
          if (!sigImg.startsWith('data:')) sigImg = 'data:image/png;base64,' + sigImg;
          const sigBoxW = adminW / 2 - 15;
          const sigBoxH = adminBlockH - 14;
          pdf.setDrawColor(180, 195, 230);
          pdf.setFillColor(255, 255, 255);
          pdf.roundedRect(cardMargin + adminW / 2, adminY + 9, sigBoxW, sigBoxH, 1, 1, 'FD');
          pdf.addImage(sigImg, 'PNG', cardMargin + adminW / 2 + 1, adminY + 10, sigBoxW - 2, sigBoxH - 2);
        } catch (imgErr) {
          pdf.setDrawColor(148, 163, 184);
          pdf.setLineDashPattern([1, 1], 0);
          pdf.line(cardMargin + adminW / 2, adminY + 20, cardMargin + adminW - 40, adminY + 20);
          pdf.setLineDashPattern([], 0);
        }
      } else {
        pdf.setDrawColor(148, 163, 184);
        pdf.setLineDashPattern([1, 1], 0);
        pdf.line(cardMargin + adminW / 2, adminY + 20, cardMargin + adminW - 40, adminY + 20);
        pdf.setLineDashPattern([], 0);
      }
      
      // Stamp area
      pdf.setFont('Roboto', 'normal');
      pdf.setFontSize(6);
      pdf.setTextColor(148, 163, 184);
      pdf.text('\u0218tampil\u0103:', cardMargin + adminW - 35, adminY + 6);
      pdf.setDrawColor(200, 210, 230);
      pdf.setLineDashPattern([2, 2], 0);
      pdf.circle(cardMargin + adminW - 20, adminY + 17, 8, 'S');
      pdf.setLineDashPattern([], 0);
      
      // Footer on all pages
      const totalPages = pdf.internal.getNumberOfPages();
      const now = new Date();
      const genDate = `Generat: ${now.getDate().toString().padStart(2,'0')}.${(now.getMonth()+1).toString().padStart(2,'0')}.${now.getFullYear()} ${now.getHours().toString().padStart(2,'0')}:${now.getMinutes().toString().padStart(2,'0')}`;
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFont('Roboto', 'normal');
        pdf.setFontSize(6);
        pdf.setTextColor(148, 163, 184);
        pdf.text(genDate, pdfW - margin, pdfH - 4, { align: 'right' });
        pdf.text(`Pagina ${i}/${totalPages}`, margin, pdfH - 4);
      }
      
      const safeCompanyName = (companyName || 'pontaj').replace(/[^a-zA-Z0-9_-]/g, '_');
      pdf.save(`${safeCompanyName}_Pontaj_${capitalizedMonth.replace(' ', '_')}.pdf`);
      toast.success("PDF grafic generat cu succes!");
    } catch (error) {
      console.error("Error generating visual PDF:", error);
      toast.error("Eroare la generarea PDF-ului grafic");
    } finally {
      setExporting(false);
    }
  };

  // Export to Excel
  const exportToExcel = async () => {
    if (!(await ensurePontajPaid())) return;
    toast.info("Se generează Excel-ul...");
    
    try {
      const monthName = new Date(year, month - 1).toLocaleString('ro-RO', { month: 'long', year: 'numeric' });
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // === Sheet 1: Calendar Pontaj ===
      const calendarData = [];
      
      // Header rows
      const headerRow1 = ['Nr.', 'Angajat'];
      const headerRow2 = ['', ''];
      days.forEach(d => {
        headerRow1.push(dayNames[d.dayOfWeek]);
        headerRow2.push(d.day);
      });
      headerRow1.push('Ore Lucrate', 'Ore Supli.', 'Zile P', 'Zile CO', 'Zile A');
      headerRow2.push('', '', '', '', '');
      
      calendarData.push(headerRow1);
      calendarData.push(headerRow2);
      
      // Worker rows - DOAR angajații selectați pentru export
      workersForExport.forEach((worker, idx) => {
        const data = allData[worker.id] || {};
        const row = [idx + 1, worker.name]; // Nume complet, nu email
        
        let totalHours = 0;
        let presentDays = 0;
        let leaveDays = 0;
        let absentDays = 0;
        
        days.forEach(d => {
          const status = getDayStatus(d.date, worker.id);
          let cellValue = '';
          
          if (status.type === 'weekend') {
            cellValue = 'W';
          } else if (status.type === 'holiday') {
            cellValue = 'SL';
          } else if (status.type === 'overtime') {
            cellValue = `OS${status.hours || ''}`;
          } else if (status.type === 'present') {
            cellValue = status.hours ? `${status.hours.toFixed(1)}h` : 'P';
            totalHours += status.hours || 8;
            presentDays++;
          } else if (['CO', 'CM', 'CS', 'FS'].includes(status.type)) {
            cellValue = status.type;
            leaveDays++;
          } else if (status.type === 'absence') {
            cellValue = 'A';
            absentDays++;
          } else if (status.type === 'invoire') {
            cellValue = 'I';
          } else if (status.type === 'delegate') {
            cellValue = 'D';
          } else {
            cellValue = '-';
          }
          
          row.push(cellValue);
        });
        
        // Ore suplimentare calculate separat
        const overtimeData = data.overtime || [];
        const overtimeHours = overtimeData.reduce((sum, o) => sum + (o.hours || 0), 0);
        
        row.push(totalHours.toFixed(1), overtimeHours > 0 ? overtimeHours.toFixed(1) : '-', presentDays, leaveDays, absentDays);
        calendarData.push(row);
      });
      
      const ws1 = XLSX.utils.aoa_to_sheet(calendarData);
      
      // Set column widths
      const colWidths = [{ wch: 5 }, { wch: 30 }]; // Nr. și Nume complet
      days.forEach(() => colWidths.push({ wch: 5 }));
      colWidths.push({ wch: 12 }, { wch: 10 }, { wch: 8 }, { wch: 8 }, { wch: 8 });
      ws1['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(wb, ws1, 'Calendar Pontaj');
      
      // === Sheet 2: Statistici Angajați ===
      const employeeData = [
        [`STATISTICI ANGAJAȚI - ${monthName.toUpperCase()}`],
        [],
        ['Nr.', 'Nume Prenume', 'Zile Prezent', 'Ore Lucrate', 'Ore Suplimentare', 'Zile CO', 'Zile CM', 'Absențe', 'Concedii (An)']
      ];
      
      // DOAR angajații selectați pentru export
      workersForExport.forEach((worker, idx) => {
        const data = allData[worker.id] || {};
        const usage = getYearlyLeaveUsage(worker.id);
        
        const presentDays = data.attendances?.length || 0;
        const totalHours = data.attendances?.reduce((sum, a) => sum + (a.hours_worked || 0), 0) || 0;
        const absenceDays = data.absences?.filter(a => a.type === 'absence').length || 0;
        
        // Ore suplimentare calculate
        const overtimeData = data.overtime || [];
        const overtimeHours = overtimeData.reduce((sum, o) => sum + (o.hours || 0), 0);
        
        // Zile CO și CM din luna curentă
        const zileCO = data.leaves?.filter(l => l.type === 'CO' && l.status === 'approved').reduce((sum, l) => sum + (l.days_count || 1), 0) || 0;
        const zileCM = data.leaves?.filter(l => l.type === 'CM' && l.status === 'approved').reduce((sum, l) => sum + (l.days_count || 1), 0) || 0;
        
        const leaveDetails = Object.entries(usage)
          .filter(([_, v]) => v > 0)
          .map(([k, v]) => `${k}: ${v}/${DEFAULT_LEAVE_LIMITS[k]}`)
          .join(', ') || 'Niciunul';
        
        employeeData.push([
          idx + 1,
          worker.name, // Nume complet
          presentDays,
          totalHours.toFixed(1) + 'h',
          overtimeHours > 0 ? overtimeHours.toFixed(1) + 'h' : '-',
          zileCO,
          zileCM,
          absenceDays,
          leaveDetails
        ]);
      });
      
      // Rând total
      const totalPresent = workersForExport.reduce((sum, w) => sum + (allData[w.id]?.attendances?.length || 0), 0);
      const totalHoursAll = workersForExport.reduce((sum, w) => sum + (allData[w.id]?.attendances?.reduce((s, a) => s + (a.hours_worked || 0), 0) || 0), 0);
      const totalOvertime = workersForExport.reduce((sum, w) => sum + ((allData[w.id]?.overtime || []).reduce((s, o) => s + (o.hours || 0), 0)), 0);
      
      employeeData.push([]);
      employeeData.push(['', 'TOTAL', totalPresent, totalHoursAll.toFixed(1) + 'h', totalOvertime > 0 ? totalOvertime.toFixed(1) + 'h' : '-', '', '', '', '']);
      
      // Add leave limits exceeded warning
      employeeData.push([]);
      employeeData.push(['LIMITE CONCEDII ANUALE:']);
      employeeData.push(['Concediu Odihnă (CO): 21 zile/an']);
      employeeData.push(['Concediu Medical (CM): 180 zile/an']);
      employeeData.push(['Concediu Special (CS): 5 zile/an']);
      employeeData.push(['Fără Salariu (FS): 30 zile/an']);
      
      const ws2 = XLSX.utils.aoa_to_sheet(employeeData);
      ws2['!cols'] = [{ wch: 5 }, { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 35 }];
      
      XLSX.utils.book_append_sheet(wb, ws2, 'Statistici Angajați');
      
      // === Sheet 3: Sărbători Legale ===
      const holidayData = [
        ['SĂRBĂTORI LEGALE ' + year, ''],
        ['', ''],
        ['Data', 'Denumire']
      ];
      
      allHolidays.forEach(h => {
        holidayData.push([
          new Date(h.date).toLocaleDateString('ro-RO', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }),
          h.name
        ]);
      });
      
      const ws3 = XLSX.utils.aoa_to_sheet(holidayData);
      ws3['!cols'] = [{ wch: 40 }, { wch: 35 }];
      
      XLSX.utils.book_append_sheet(wb, ws3, 'Sărbători Legale');
      
      // Generate and download
      XLSX.writeFile(wb, `${safeCompanyFileSlug}_Pontaj_${monthName.replace(' ', '_')}.xlsx`);
      toast.success("Excel generat cu succes!");
      
    } catch (error) {
      console.error("Error generating Excel:", error);
      toast.error("Eroare la generarea Excel-ului");
    }
  };

  const renderDayCell = (date, worker) => {
    const status = getDayStatus(date, worker.id);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const isHoliday = status.isHoliday;
    const dateStr = toLocalDateStr(date);
    const cellId = `${worker.id}_${dateStr}`;
    const isFreeDay = isWeekend || isHoliday;
    
    const cellContent = (
      <div 
        className={`h-16 ${status.color} flex items-center justify-center relative group transition-all
          cursor-pointer hover:scale-105 hover:shadow-lg hover:z-10
          ${isHoliday ? 'holiday-cell' : ''}
          ${status.overLimit ? 'ring-2 ring-red-700' : ''}
          ${isFreeDay && status.type === 'overtime' ? 'ring-2 ring-amber-500' : ''}
        `}
        data-testid={`cell-${worker.id.substring(0,8)}-${date.getDate()}`}
        title={`${date.getDate()} - ${status.label}${status.overLimit ? ' ⚠️ DEPĂȘIT LIMITA!' : ''}${isFreeDay ? ' (Zi liberă - click pentru ore suplimentare)' : ''}`}
        onClick={() => handleCellClick(worker, date, status)}
      >
        {/* Holiday hatch pattern overlay */}
        {isHoliday && (
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 4px,
                rgba(0,0,0,0.15) 4px,
                rgba(0,0,0,0.15) 8px
              )`
            }}
          />
        )}
        
        {/* Over limit warning icon */}
        {status.overLimit && (
          <AlertTriangle className="absolute top-0.5 right-0.5 w-3 h-3 text-red-900" />
        )}
        
        {status.type === 'overtime' && (
          <span className="text-xs font-bold text-white">{status.hours ? `${status.hours}h` : 'OS'}</span>
        )}
        {status.hours && status.type === 'present' && (
          <span className={`text-xs font-medium relative z-10 text-white`}>
            {status.hours.toFixed(1)}h
          </span>
        )}
        {status.type === 'no-data' && (
          <span className="text-xs text-slate-300">-</span>
        )}
        {status.type === 'holiday' && !status.isOvertime && (
          <span className="text-xs font-bold text-yellow-800 relative z-10">SL</span>
        )}
        {['CO', 'CM', 'CS', 'FS'].includes(status.type) && (
          <span className="text-xs font-bold text-white">{status.type}</span>
        )}
        {status.type === 'absence' && (
          <span className="text-xs font-bold text-white">A</span>
        )}
        {status.type === 'delegate' && (
          <span className="text-xs font-bold text-white">D</span>
        )}
        {status.type === 'invoire' && (
          <span className="text-xs font-bold text-white">I</span>
        )}
        
        {/* Tooltip on hover */}
        <div className="absolute hidden group-hover:block bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-3 py-2 rounded whitespace-nowrap z-30 shadow-xl">
          <div className="font-semibold">{date.getDate()} {date.toLocaleString('ro-RO', {month: 'long'})}</div>
          <div>{status.label}</div>
          {status.hours && <div>{status.hours.toFixed(1)} ore</div>}
          {status.equivalentHours && <div className="text-amber-300">= {status.equivalentHours.toFixed(1)} ore (1.5x)</div>}
          {status.overLimit && <div className="text-red-400 font-bold">⚠️ Limită depășită!</div>}
          {isFreeDay && status.type !== 'overtime' && <div className="text-amber-300 mt-1">Click pentru ore suplimentare</div>}
          {!isFreeDay && <div className="text-blue-300 mt-1">Click pentru editare</div>}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-black"></div>
        </div>
      </div>
    );

    return cellContent;
  };

  return (
    <div className="space-y-4">
      {/* Header with Export Buttons and Collapsible Legend */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-between gap-3">
        {/* Collapsible Legend Button */}
        <Button
          variant="outline"
          onClick={() => setLegendOpen(!legendOpen)}
          className="flex items-center justify-center gap-2 border-slate-300 hover:bg-slate-100 h-10 px-3 text-sm w-full sm:w-auto"
          data-testid="legend-toggle-button"
        >
          <Info className="w-4 h-4" />
          Legendă
          {legendOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>

        {/* Export Buttons - grid 2x2 pe mobile, inline pe desktop */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 items-stretch sm:items-center w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => setShowExportSelection(!showExportSelection)}
            className={`border-amber-400 hover:bg-amber-50 h-auto py-2 px-2 flex-col sm:flex-row gap-0 sm:gap-2 ${showExportSelection ? 'bg-amber-100' : ''}`}
            data-testid="select-workers-export"
          >
            <Settings2 className="w-4 h-4" />
            <span className="text-[10px] sm:text-sm leading-tight mt-0.5 sm:mt-0">
              <span className="sm:hidden">Selectare</span>
              <span className="hidden sm:inline">Selectare</span>
              <span className="ml-1 text-slate-500">({workersForExport.length}/{workers.length})</span>
            </span>
          </Button>

          <Button
            onClick={exportToPDF}
            disabled={exporting || workersForExport.length === 0}
            className="bg-blue-600 hover:bg-blue-700 h-auto py-2 px-2 flex-col sm:flex-row gap-0 sm:gap-2"
            data-testid="export-pdf-button"
          >
            <Download className="w-4 h-4" />
            <span className="text-[10px] sm:text-sm leading-tight mt-0.5 sm:mt-0">{exporting ? "..." : "PDF"}</span>
          </Button>

          <Button
            onClick={exportToVisualPDF}
            disabled={exporting}
            className="bg-teal-600 hover:bg-teal-700 h-auto py-2 px-2 flex-col sm:flex-row gap-0 sm:gap-2"
            data-testid="export-visual-pdf-button"
          >
            <Image className="w-4 h-4" />
            <span className="text-[10px] sm:text-sm leading-tight mt-0.5 sm:mt-0">{exporting ? "..." : "PDF Grafic"}</span>
          </Button>

          <Button
            onClick={exportToExcel}
            disabled={workersForExport.length === 0}
            className="bg-green-600 hover:bg-green-700 h-auto py-2 px-2 flex-col sm:flex-row gap-0 sm:gap-2"
            data-testid="export-excel-button"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span className="text-[10px] sm:text-sm leading-tight mt-0.5 sm:mt-0">Excel</span>
          </Button>
        </div>
      </div>

      {/* Panel selecție angajați pentru export */}
      {showExportSelection && (
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-amber-600" />
              <span className="font-semibold text-amber-800">Selectează angajații pentru document</span>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => toggleAllForExport(true)}
                className="text-xs border-amber-400"
              >
                Selectează toți
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => toggleAllForExport(false)}
                className="text-xs border-amber-400"
              >
                Deselectează toți
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
            {workers.map(worker => (
              <label 
                key={worker.id}
                className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-all
                  ${selectedForExport[worker.id] 
                    ? 'bg-green-100 border-green-400' 
                    : 'bg-white border-slate-200 hover:border-slate-400'}
                  ${worker.is_administrator ? 'ring-2 ring-amber-400' : ''}
                `}
              >
                <Checkbox 
                  checked={selectedForExport[worker.id] || false}
                  onCheckedChange={() => toggleWorkerExport(worker.id)}
                  data-testid={`export-checkbox-${worker.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{worker.name}</div>
                  {worker.is_administrator && (
                    <Badge className="bg-amber-500 text-xs mt-0.5">Admin</Badge>
                  )}
                </div>
              </label>
            ))}
          </div>
          
          <p className="text-xs text-amber-700 mt-3">
            ⚠️ Administratorii sunt deselectați implicit. Bifați-i dacă doriți să apară pe documentul final.
          </p>
        </div>
      )}

      {/* Collapsible Legend Content */}
      {legendOpen && (
        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 animate-in slide-in-from-top-2 duration-200">
          {/* Rândul 1: Prezență și Ore */}
          <div className="flex flex-wrap gap-3 text-sm mb-3">
            <Badge className="bg-green-500 px-3 py-1.5">P - Prezent</Badge>
            <Badge className="bg-green-300 text-green-800 px-3 py-1.5">Auto</Badge>
            <Badge className="bg-amber-500 px-3 py-1.5">OS - Ore Suplimentare</Badge>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-400 rounded-full text-yellow-900 font-medium"
              style={{
                backgroundImage: `repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 2px,
                  rgba(0,0,0,0.1) 2px,
                  rgba(0,0,0,0.1) 4px
                )`
              }}
            >
              SL - Sărbătoare
            </div>
            <Badge className="bg-slate-300 text-slate-700 px-3 py-1.5">Weekend</Badge>
          </div>
          
          {/* Rândul 2: Concedii și Absențe */}
          <div className="flex flex-wrap gap-3 text-sm">
            <Badge className="bg-orange-400 px-3 py-1.5">CO - Concediu Odihnă</Badge>
            <Badge className="bg-red-400 px-3 py-1.5">CM - Concediu Medical</Badge>
            <Badge className="bg-purple-400 px-3 py-1.5">CS - Concediu Special</Badge>
            <Badge className="bg-pink-400 px-3 py-1.5">FS - Fără Salariu</Badge>
            <Badge className="bg-cyan-400 px-3 py-1.5">I - Învoire</Badge>
            <Badge className="bg-blue-400 px-3 py-1.5">D - Delegație</Badge>
            <Badge className="bg-gray-600 text-white px-3 py-1.5">A - Absent</Badge>
          </div>
          
          {/* Romanian National Holidays for current month */}
          {(() => {
            const monthHolidays = allHolidays.filter(h => {
              const hDate = new Date(h.date);
              return hDate.getMonth() + 1 === month && hDate.getFullYear() === year;
            });
            
            if (monthHolidays.length > 0) {
              return (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-xs font-semibold text-slate-700 mb-2">
                    Sărbători legale în {new Date(year, month - 1).toLocaleString('ro-RO', { month: 'long', year: 'numeric' })}:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {monthHolidays.map((h) => (
                      <span 
                        key={h.date}
                        className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full border border-yellow-300"
                      >
                        <span className="font-semibold">{new Date(h.date).getDate()}</span>
                        <span>-</span>
                        <span>{h.name}</span>
                      </span>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* Instruction */}
      <p className="text-sm text-slate-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
        <strong>Click</strong> pe celulă pentru editare pontaj
      </p>

      {/* Leave Limits Warning */}
      {workers.some(w => {
        const usage = getYearlyLeaveUsage(w.id);
        return Object.entries(usage).some(([type, days]) => days >= DEFAULT_LEAVE_LIMITS[type]);
      }) && (
        <div className="bg-red-50 border border-red-300 p-3 rounded-lg flex items-center gap-2 text-red-800">
          <AlertTriangle className="w-5 h-5" />
          <span className="text-sm font-medium">
            Atenție! Unii angajați au depășit limita de zile de concediu (celulele pulsează roșu)
          </span>
        </div>
      )}

        {/* Calendar Grid */}
        <div ref={calendarRef} className="bg-white rounded-lg border border-slate-200 overflow-x-auto shadow-sm">
          <div className="inline-block min-w-full">
            <table className="min-w-full border-collapse">
              <thead>
                {/* Day of week row */}
                <tr className="bg-slate-50">
                  <th className="sticky left-0 z-20 bg-slate-50 border-r-2 border-slate-300 p-2 text-left font-semibold text-sm min-w-[180px]">
                    Angajat
                  </th>
                  {days.map((d) => (
                    <th 
                      key={`dow-${d.day}`} 
                      className={`text-center text-xs font-medium p-1 w-12 ${
                        d.dayOfWeek === 0 || d.dayOfWeek === 6 ? 'bg-slate-100' : ''
                      }`}
                    >
                      {dayNames[d.dayOfWeek]}
                    </th>
                  ))}
                </tr>
                {/* Day number row */}
                <tr className="bg-slate-100 border-b-2 border-slate-300">
                  <th className="sticky left-0 z-20 bg-slate-100 border-r-2 border-slate-300 p-2 text-left text-xs text-slate-600">
                    {new Date(year, month - 1).toLocaleString('ro-RO', {month: 'long', year: 'numeric'})}
                  </th>
                  {days.map((d) => {
                    const dateStr = toLocalDateStr(d.date);
                    const isHoliday = holidayDates.has(dateStr);
                    
                    return (
                      <th 
                        key={`day-${d.day}`}
                        className={`text-center font-bold text-sm p-1 w-12 ${
                          d.dayOfWeek === 0 || d.dayOfWeek === 6 
                            ? 'text-red-600 bg-slate-150' 
                            : isHoliday 
                              ? 'text-yellow-700 bg-yellow-100' 
                              : 'text-slate-700'
                        }`}
                        title={isHoliday ? getHolidayName(dateStr) : ''}
                      >
                        {d.day}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {workers.map((worker, idx) => {
                  const usage = getYearlyLeaveUsage(worker.id);
                  const hasOverLimit = Object.entries(usage).some(
                    ([type, days]) => days >= DEFAULT_LEAVE_LIMITS[type]
                  );
                  
                  return (
                    <tr 
                      key={worker.id}
                      className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-blue-50 transition-colors ${hasOverLimit ? 'bg-red-50' : ''}`}
                    >
                      <td className={`sticky left-0 z-10 bg-inherit border-r-2 border-slate-300 p-3 font-semibold text-sm ${hasOverLimit ? 'bg-red-50' : ''}`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs ${hasOverLimit ? 'bg-red-500' : 'bg-gradient-to-br from-teal-500 to-blue-600'}`}>
                            {worker.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
                          </div>
                          <div>
                            <span className="truncate max-w-[140px] block text-xs" title={worker.name}>
                              {worker.name}
                            </span>
                            {hasOverLimit && (
                              <span className="text-xs text-red-600 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Limită depășită
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      {days.map((d) => (
                        <td key={`${worker.id}-${d.day}`} className="p-0 border border-slate-100">
                          {renderDayCell(d.date, worker)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Stats - computed from getDayStatus() to match the grid exactly */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {workers.map((worker) => {
            // Compute stats by iterating each day using getDayStatus (same as grid)
            let presentDays = 0;
            let totalHours = 0;
            let leaveDays = 0;
            let absenceDays = 0;
            let overtimeHours = 0;
            let overtimeEquivalent = 0;
            let overtimeOnWeekend = false;
            let overtimeOnHoliday = false;
            
            days.forEach(d => {
              const status = getDayStatus(d.date, worker.id);
              if (status.type === 'present') {
                presentDays++;
                totalHours += status.hours || 8;
              } else if (status.type === 'overtime') {
                overtimeHours += status.hours || 0;
                overtimeEquivalent += status.equivalentHours || (status.hours || 0) * 1.5;
                if (status.isOnWeekend) overtimeOnWeekend = true;
                if (status.isOnHoliday) overtimeOnHoliday = true;
              } else if (['CO', 'CM', 'CS', 'FS'].includes(status.type)) {
                leaveDays++;
              } else if (status.type === 'absence') {
                absenceDays++;
              }
            });
            
            const usage = getYearlyLeaveUsage(worker.id);
            const hasOverLimit = Object.entries(usage).some(
              ([type, days]) => days >= DEFAULT_LEAVE_LIMITS[type]
            );
            
            return (
              <div key={worker.id} className={`p-4 rounded-lg border ${hasOverLimit ? 'bg-red-50 border-red-300' : 'bg-gradient-to-br from-slate-50 to-blue-50 border-slate-200'}`}>
                <p className="font-semibold text-sm mb-2 flex items-center gap-2" title={worker.name}>
                  {worker.name}
                  {hasOverLimit && <AlertTriangle className="w-4 h-4 text-red-600" />}
                </p>
                <div className="space-y-1 text-xs text-slate-600">
                  <div className="flex justify-between">
                    <span>Zile prezent:</span>
                    <span className="font-semibold text-green-600">{presentDays}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Zile concediu (luna):</span>
                    <span className="font-semibold text-orange-600">{leaveDays}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Zile absente:</span>
                    <span className="font-semibold text-gray-600">{absenceDays}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Ore lucrate:</span>
                    <span className="font-semibold text-blue-600">{totalHours.toFixed(1)}h</span>
                  </div>
                  {/* Ore suplimentare — afișate doar dacă: total ore > norma (160h/lună) SAU OS în weekend/sărbătoare */}
                  {overtimeHours > 0 && (totalHours > 160 || overtimeOnWeekend || overtimeOnHoliday) && (
                    <div className="flex justify-between pt-1 border-t border-amber-200 mt-1">
                      <span className="text-amber-700 font-medium">Ore suplimentare:</span>
                      <span className="font-semibold text-amber-600" title={`Echivalent: ${overtimeEquivalent.toFixed(1)}h (x1.5)${overtimeOnWeekend ? ' · weekend' : ''}${overtimeOnHoliday ? ' · sărbătoare' : ''}`}>
                        {overtimeHours.toFixed(1)}h
                      </span>
                    </div>
                  )}
                  {/* Yearly leave usage */}
                  <div className="pt-2 mt-2 border-t border-slate-200">
                    <p className="font-semibold text-slate-700 mb-1">Concedii An:</p>
                    {Object.entries(usage).filter(([_, d]) => d > 0).map(([type, days]) => (
                      <div key={type} className="flex justify-between">
                        <span>{type}:</span>
                        <span className={`font-semibold ${days >= DEFAULT_LEAVE_LIMITS[type] ? 'text-red-600' : 'text-slate-600'}`}>
                          {days}/{DEFAULT_LEAVE_LIMITS[type]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Edit Dialog */}
        <AttendanceEditDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          worker={selectedWorker}
          date={selectedDate}
          currentStatus={selectedStatus}
          onSave={handleSave}
        />
      </div>
  );
}
